import type { CompetitorFilters } from '../types/dashboard.types';

export type ExploreApiPrefix = '/dashboard' | '/overview' | '/drilldown';

export interface CompetitorFilterDefaultsData {
  defaultBrand: string | null;
  defaultIndustry: string | null;
  competitors: Array<{
    industry: string;
    mainCompetitor: string | null;
    industryCompetitors: string | null;
    /** Admin-set My Brand for this industry (AD-2790). Optional while the API rolls out. */
    defaultBrand?: string | null;
  }>;
}

export interface CompetitorSettingsRow {
  industry: string;
  mainCompetitor: string | null;
  industryCompetitors: string | null;
}

export interface BootstrapCompetitorFilterDefaultsPayload {
  myIndustry: string;
  myBrand: string;
  competitorsIndustry: string;
  mainCompetitor: string;
  benchmark: string[];
}

export function resolveStartingIndustry(args: {
  defaultIndustry: string | null | undefined;
  industryIndexes: string[];
}): string | null {
  const { defaultIndustry, industryIndexes } = args;
  if (industryIndexes.length === 0) return null;
  if (defaultIndustry && industryIndexes.includes(defaultIndustry)) {
    return defaultIndustry;
  }
  return industryIndexes[0] ?? null;
}

/**
 * Replaces a My Brand that is not in its industry's roster — from a preset, a deep
 * link, or state hydrated before the roster loaded — with the industry default.
 * Filters for a different industry than the roster are returned unchanged.
 */
export function pruneMyBrandToRoster(args: {
  filters: CompetitorFilters;
  rosterIndustry: string | null;
  rosterBrandNames: readonly string[];
  fallbackBrand: string | null;
}): CompetitorFilters {
  const { filters, rosterIndustry, rosterBrandNames, fallbackBrand } = args;
  if (filters.myIndustry !== rosterIndustry || filters.myBrand === null) return filters;
  if (rosterBrandNames.includes(filters.myBrand)) return filters;
  return { ...filters, myBrand: fallbackBrand };
}

export function findCompetitorSettingsForIndustry(
  rows: CompetitorSettingsRow[],
  industry: string | null,
): CompetitorSettingsRow | undefined {
  if (!industry) return undefined;
  return rows.find((row) => row.industry === industry);
}

/** Case-insensitive BQ key match on the raw `competitor` key. */
export function findBrandImageUrl(
  brands: readonly { name: string; imageUrl?: string | null }[],
  brandName: string | null | undefined,
): string | null {
  if (!brandName) return null;
  const key = brandName.trim().toLowerCase();
  if (!key) return null;
  const match = brands.find((brand) => brand.name.trim().toLowerCase() === key);
  return match?.imageUrl ?? null;
}

export function buildBootstrapPayload(args: {
  defaults: CompetitorFilterDefaultsData | undefined;
  appliedFilters: CompetitorFilters;
}): BootstrapCompetitorFilterDefaultsPayload | null {
  const { defaults, appliedFilters } = args;
  const {
    myIndustry,
    myBrand,
    competitorsIndustry,
    mainCompetitor,
    benchmark,
  } = appliedFilters;

  if (!myIndustry || !myBrand || !competitorsIndustry || !mainCompetitor || benchmark.length === 0) {
    return null;
  }

  const industryKey = competitorsIndustry || myIndustry;
  const competitorRow = findCompetitorSettingsForIndustry(defaults?.competitors ?? [], industryKey);
  const hasIndustryDefaultBrand = !!defaults?.competitors.find((row) => row.industry === myIndustry)
    ?.defaultBrand;

  const needsTenantBrand = !defaults?.defaultBrand && !!myBrand && !hasIndustryDefaultBrand;
  const needsTenantIndustry = !defaults?.defaultIndustry && !!myIndustry;
  const needsMainCompetitor = !competitorRow?.mainCompetitor && !!mainCompetitor;
  const needsBenchmark = !competitorRow?.industryCompetitors && benchmark.length > 0;

  if (!needsTenantBrand && !needsTenantIndustry && !needsMainCompetitor && !needsBenchmark) {
    return null;
  }

  return {
    myIndustry,
    myBrand,
    competitorsIndustry,
    mainCompetitor,
    benchmark,
  };
}
